import React, { FC, memo, useEffect, useMemo, useState } from 'react';
import { Box, makeStyles, Typography } from '@material-ui/core';
import { colors, drjInfo } from 'src/constants';
import { Logo } from 'src/components';
import { dateToday } from 'src/utils';
import clsx from 'clsx';
import { slices, useAppSelector } from 'src/redux';
import { NewBranchData } from 'src/types';
import useQuotationProduct from 'src/hooks/quotation-v2/use-quotation-products';
import QRCode from 'qrcode';

const { selectors: userSelectors } = slices.user;

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    height: 150,
    width: '100%',
    borderRadius: 20,
    position: 'relative'
  },
  info: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    marginRight: '17.8%'
  },
  text: {
    textAlign: 'center'
  },
  link: {
    textDecoration: 'underline'
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center'
  },
  title: {
    fontWeight: 700,
    letterSpacing: 0.5,
    color: colors.primary
  },
  templateQrImg: { height: '8em', width: '8em' },
  id: { fontSize: 5.5, textAlign: 'center' },
  qrContainer: { textAlign: 'center', position: 'absolute', right: 0 },
  shoutOutContainer: {
    marginTop: theme.spacing(1)
  },
  shoutOut: {
    fontWeight: 500,
    letterSpacing: 0.75,
    color: colors.red[800]
  },
  salesId: {
    letterSpacing: 1,
    fontSize: 12,
    color: colors.black
  }
}));

interface Props {
  branchInfo?: NewBranchData;
}
const Component: FC<Props> = ({ branchInfo }) => {
  const classes = useStyles();
  const userInfo = useAppSelector(userSelectors.selectUserInfo);
  const { templateDetails } = useQuotationProduct();

  const employeeName = useMemo(() => {
    const name = `${userInfo?.id}-${userInfo?.first_name} ${userInfo?.last_name}`;
    return name;
  }, [userInfo]);

  const [uriQR, setUriQR] = useState<string>('');

  const generateQR = async (id: string) => {
    if (!id) {
      return;
    }
    try {
      const quotationIdUriStr = id;
      const url = await QRCode.toDataURL(quotationIdUriStr, {
        errorCorrectionLevel: 'L',
        margin: 1
      });
      setUriQR(url);
    } catch (err) {
      console.error('QR generation failed:', err);
    }
  };

  useEffect(() => {
    generateQR(String(templateDetails?.id));
  }, [templateDetails]);

  return (
    <Box className={classes.root}>
      <Box className="logoContainer">
        <Box
          style={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Logo size={100} />
        </Box>
        <Typography className={classes.salesId}>
          {/* {`Sales: ${userInfo?.id}-${employeeName}`} */}
          {`Sales:${employeeName}`}
        </Typography>
        <Typography className={classes.salesId}>
          {`Date: ${dateToday()}`}
        </Typography>
      </Box>

      <Box className={classes.info}>
        <div className={classes.titleContainer}>
          <Typography className={clsx(classes.text)}>
            Owned and Operated by:
            <Typography
              style={{ marginLeft: 4 }}
              className={clsx(classes.text, classes.title)}
            >
              {drjInfo?.name || ''}
            </Typography>
          </Typography>
        </div>

        <div className={classes.titleContainer}>
          <Typography className={clsx(classes.text, classes.title)}>
            Location:
          </Typography>
          <Typography style={{ marginLeft: 4 }} className={clsx(classes.text)}>
            {branchInfo?.address || drjInfo.location || ''}
          </Typography>
        </div>

        <div className={classes.titleContainer}>
          <div className={classes.titleContainer}>
            <Typography className={clsx(classes.text, classes.title)}>
              Website:
            </Typography>
            <Typography
              style={{
                marginLeft: 4,
                color: colors.link,
                textDecoration: 'underline'
              }}
              className={clsx(classes.text)}
            >
              {`www.${drjInfo.website} `}
            </Typography>
          </div>

          <div style={{ marginLeft: 10 }} className={classes.titleContainer}>
            <Typography className={clsx(classes.text, classes.title)}>
              FB Page:
            </Typography>
            <Typography
              style={{
                marginLeft: 4,
                color: colors.link,
                textDecoration: 'underline'
              }}
              className={clsx(classes.text)}
            >
              {`${drjInfo.facebook} `}
            </Typography>
          </div>
        </div>

        <div className={classes.titleContainer}>
          <div className={classes.titleContainer}>
            <Typography className={clsx(classes.text, classes.title)}>
              Email:
            </Typography>
            <Typography
              style={{
                marginLeft: 4
              }}
              className={clsx(classes.text)}
            >
              {`${drjInfo.email} `}
            </Typography>
          </div>

          <div style={{ marginLeft: 10 }} className={classes.titleContainer}>
            <Typography className={clsx(classes.text, classes.title)}>
              Viber:
            </Typography>
            <Typography
              style={{
                marginLeft: 4,
                color: colors.link,
                textDecoration: 'underline'
              }}
              className={clsx(classes.text)}
            >
              {`${drjInfo.cellNo2} `}
            </Typography>
          </div>
        </div>

        <div style={{ marginLeft: 10 }} className={classes.titleContainer}>
          <Typography className={clsx(classes.text, classes.title)}>
            Contact Details:
          </Typography>

          <Typography className={classes.text}>
            {/* {concatMobileNo || drjInfo.cellNo} {' / '} */}
            {branchInfo?.tel_no || drjInfo.telNo}
          </Typography>
        </div>
      </Box>

      {templateDetails?.id ? (
        <Box className={classes.qrContainer}>
          <img className={classes.templateQrImg} src={uriQR} />
          <Typography>{templateDetails?.id}</Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export const QuotationHeader = memo(Component);
